import React, { useEffect, useMemo, useState } from 'react';
import { updateProfile } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { toast } from 'react-hot-toast';
import { db, functions, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { compressImage } from '../utils/imageCompression';
import { getGoogleAuthErrorMessage } from '../utils/authErrorUtils';

const MAX_DISPLAY_NAME_LENGTH = 40;

function isAppCheckFailure(error) {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return code === 'functions/failed-precondition'
    || code === 'failed-precondition'
    || message.includes('App Check verified app');
}

function ProfileFinisherForm({
  source = 'signup',
  submitLabel = 'Save and Continue',
  showSkip = true,
  skipLabel = 'Skip for Now',
  onComplete,
  onSkip,
  className = '',
}) {
  const { user, userDoc } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const resolvedName = userDoc?.displayName || user?.displayName || '';
    const resolvedPhoto = userDoc?.photoURL || user?.photoURL || '';
    setDisplayName(resolvedName);
    setProfileImagePreview(resolvedPhoto);
  }, [user?.displayName, user?.photoURL, userDoc?.displayName, userDoc?.photoURL]);

  const subtitle = useMemo(() => {
    if (source === 'google') {
      return 'Make sure your display name and profile photo look exactly how you want before you start.';
    }
    return 'Add an optional profile photo now, or come back later in Settings.';
  }, [source]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!String(file.type || '').startsWith('image/')) {
      setError('Profile image must be an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Profile image must be less than 5MB');
      return;
    }
    setProfileImage(file);
    const reader = new FileReader();
    reader.onload = () => setProfileImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const markGoogleProfileComplete = async () => {
    if (!user?.uid) return;
    await setDoc(doc(db, 'users', user.uid), {
      googleProfileCompletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const persistProfileDirectly = async ({ normalizedName, photoURL }) => {
    if (!user?.uid) return;

    const safeProfilePatch = {
      displayName: normalizedName,
      photoURL: photoURL || '',
      updatedAt: serverTimestamp(),
    };

    await Promise.all([
      setDoc(doc(db, 'users', user.uid), safeProfilePatch, { merge: true }),
      setDoc(doc(db, 'publicProfiles', user.uid), safeProfilePatch, { merge: true }),
    ]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const normalizedName = String(displayName || '').trim();
    if (!normalizedName) {
      setError('Display name is required');
      return;
    }
    if (normalizedName.length > MAX_DISPLAY_NAME_LENGTH) {
      setError(`Display name must be ${MAX_DISPLAY_NAME_LENGTH} characters or less`);
      return;
    }

    setLoading(true);
    try {
      let photoURL = userDoc?.photoURL || user?.photoURL || '';
      if (profileImage) {
        const compressed = await compressImage(profileImage);
        const imageRef = ref(storage, `profileImages/${user.uid}/${Date.now()}-${profileImage.name}`);
        await uploadBytes(imageRef, compressed);
        photoURL = await getDownloadURL(imageRef);
      }

      try {
        await updateProfile(user, {
          displayName: normalizedName,
          photoURL: photoURL || null,
        });
      } catch (authErr) {
        console.warn('Auth profile update failed', authErr);
      }

      const updateUserProfile = httpsCallable(functions, 'updateUserProfile');
      try {
        await updateUserProfile({
          displayName: normalizedName,
          photoURL: photoURL || '',
        });
      } catch (callableErr) {
        if (!isAppCheckFailure(callableErr)) {
          throw callableErr;
        }
        await persistProfileDirectly({ normalizedName, photoURL });
      }

      if (source === 'google') {
        await markGoogleProfileComplete();
      }

      toast.success('Profile saved');
      if (onComplete) onComplete();
    } catch (err) {
      console.error('Profile finisher save failed', err);
      setError(getGoogleAuthErrorMessage(err, 'We could not save your profile. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`complete-profile-shell ${className}`.trim()}>
      <p className="complete-profile-eyebrow">Welcome to Duckerr</p>
      <h1>{source === 'google' ? 'Finish your profile' : 'Review your profile'}</h1>
      <p className="complete-profile-subtitle">{subtitle}</p>

      {error ? <p className="error" role="alert">{error}</p> : null}

      <form className="complete-profile-form" onSubmit={handleSubmit}>
        <div className="complete-profile-avatar-row">
          <div className="complete-profile-avatar">
            {profileImagePreview ? (
              <img src={profileImagePreview} alt="Profile preview" className="complete-profile-avatar-image" />
            ) : (
              <span className="complete-profile-avatar-placeholder">👤</span>
            )}
          </div>
          <div className="complete-profile-avatar-actions">
            <input
              id={`completeProfileImage-${source}`}
              type="file"
              accept="image/*"
              className="complete-profile-file-input"
              onChange={handleImageChange}
            />
            <label htmlFor={`completeProfileImage-${source}`} className="btn btn-outline">
              Choose Photo
            </label>
            {(profileImagePreview || profileImage) ? (
              <button
                type="button"
                className="complete-profile-remove"
                onClick={() => {
                  setProfileImage(null);
                  setProfileImagePreview('');
                }}
              >
                Remove
              </button>
            ) : null}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor={`completeProfileName-${source}`}>Display Name</label>
          <input
            id={`completeProfileName-${source}`}
            type="text"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            autoComplete="name"
            maxLength={MAX_DISPLAY_NAME_LENGTH}
          />
          <small>This is what other duckers will see.</small>
        </div>

        <div className="complete-profile-actions">
          <button type="submit" className="btn auth-btn" disabled={loading}>
            {loading ? 'Saving...' : submitLabel}
          </button>
          {showSkip && onSkip ? (
            <button type="button" className="btn btn-outline" onClick={onSkip} disabled={loading}>
              {skipLabel}
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}

export default ProfileFinisherForm;
